import { getHexTileMapKey } from '../../../hexmap/model/keys';
import {
  type Building,
  computeVision,
  type EntityId,
  engagements,
  isCity,
  type PlayerId,
  type SimContext,
  type SimState,
  type Unit,
} from '../../../sim';
import type { EntitySummary } from '../summary';
import { entityName, pluralName, targetOf, whereFrom } from '../text';
import { ATTACK_MERGE_RADIUS, ATTACK_PRIORITY, type GameEvent, hexDistance } from '../types';
import type { DiffContext } from './context';

/**
 * An own building hit since the last summary (hp dropped, or a rival hitting
 * it this tick, which catches one mended as fast as it's hit): a city with
 * its hp, outranking other hits in its area.
 */
export function buildingAttacked(c: DiffContext, id: EntityId, b: EntitySummary): GameEvent | null {
  if (b.owner !== c.playerId) return null;
  const before = c.prev.buildings.get(id);
  if (!before || !(b.hp < before.hp || c.engaged?.has(id))) return null;
  if (isCity(b, c.rules)) {
    const hp = `${Math.ceil(b.hp).toLocaleString('en-US')}${
      b.maxHp !== undefined ? `/${Math.ceil(b.maxHp).toLocaleString('en-US')}` : ''
    } hp`;
    return {
      kind: 'attack',
      text: `${c.cityLabel?.(id) ?? 'Your City'} under attack · ${hp}`,
      pos: b.pos,
      entityId: id,
      priority: ATTACK_PRIORITY.city,
    };
  }
  return {
    kind: 'attack',
    text: `Your ${c.buildingName(b.type)} is under attack`,
    pos: b.pos,
    entityId: id,
    priority: ATTACK_PRIORITY.building,
  };
}

/** An own unit hit since the last summary (`before`), as `buildingAttacked`. */
export function unitAttacked(
  c: DiffContext,
  id: EntityId,
  u: EntitySummary,
  before: EntitySummary
): GameEvent | null {
  if (!(u.hp < before.hp || c.engaged?.has(id))) return null;
  return {
    kind: 'attack',
    text: `Your ${c.unitName(u.type)} is under attack`,
    pos: u.pos,
    entityId: id,
  };
}

/**
 * Scouts and spies killed (their owner still in the match: elimination
 * removes everything). A spy lost in a rival's land names whose. They fold
 * into the attack entry there, whose text they take.
 */
export function scoutsLost(c: DiffContext): GameEvent[] {
  if (c.cities.length === 0) return [];
  const events: GameEvent[] = [];
  for (const [id, u] of c.prev.units) {
    if (u.owner !== c.playerId || c.next.units.has(id)) continue;
    const def = c.rules.units[u.type];
    if (def?.automate !== 'explore' && !def?.stealth) continue;
    const where = u.rivalTerritory
      ? `in ${c.playerName(u.rivalTerritory)}'s city`
      : whereFrom(u.pos, c.cities, c.cityLabel);
    events.push({
      kind: 'attack',
      text: `${c.unitName(u.type)} lost ${where}`,
      pos: u.pos,
      unitLost: true,
    });
  }
  return events;
}

/**
 * Gives each attack event a `target`: a rival hitting the attacked unit or
 * building this tick, on a tile the player sees (lowest id first), else the
 * attacked spot itself, so troops sent there fight whoever is around. Also
 * its `attackers`: the rivals in sight hitting any own unit or building
 * within ATTACK_MERGE_RADIUS of it (the area its entry covers), by type,
 * most first ("2 Artillery, 1 Tank"). `vision` as in `discoveryEvents`
 * (asked only when there's an attack event); `engaged` likewise gives this
 * tick's `engagements` when the caller has them.
 */
export function withAttackers(
  events: GameEvent[],
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  vision?: () => ReadonlySet<string>,
  engaged: () => ReadonlyMap<EntityId, EntityId> = () => engagements(state, ctx),
  playerName: (id: PlayerId) => string = (id) => id
): GameEvent[] {
  if (!events.some((e) => e.kind === 'attack')) return events;
  const seen = vision ? vision() : computeVision(state, ctx.rules, playerId);
  const hits = [...engaged()].sort(
    ([a], [b]) => Number(a.slice(1)) - Number(b.slice(1)) || a.localeCompare(b)
  );
  // Visible rivals hitting one of the player's units or buildings.
  const seenHits: Array<{ attacker: Unit | Building; target: Unit | Building }> = [];
  for (const [attackerId, targetId] of hits) {
    const attacker = state.units[attackerId] ?? state.buildings[attackerId];
    const target = state.units[targetId] ?? state.buildings[targetId];
    if (!attacker || !target || attacker.owner === playerId || target.owner !== playerId) continue;
    if (!seen.has(getHexTileMapKey(attacker.pos))) continue;
    seenHits.push({ attacker, target });
  }
  return events.map((e) => {
    if (e.kind !== 'attack' || !e.pos) return e;
    const pos = e.pos;
    const counts = new Map<string, number>();
    for (const { attacker, target } of seenHits) {
      if (hexDistance(target.pos, pos) > ATTACK_MERGE_RADIUS) continue;
      const name = entityName(ctx.rules, attacker);
      counts.set(name, (counts.get(name) ?? 0) + 1);
    }
    const attackers = [...counts]
      .sort(([a, m], [b, n]) => n - m || a.localeCompare(b))
      .map(([name, n]) => `${n} ${n === 1 ? name : pluralName(name)}`)
      .join(', ');
    const named = attackers ? { ...e, attackers } : e;
    const hit = seenHits.find(({ target }) => target.id === e.entityId);
    if (hit) return { ...named, target: targetOf(ctx.rules, hit.attacker, playerName) };
    return { ...named, target: { pos: { ...pos }, label: 'the attackers there' } };
  });
}
